import { useCallback, useEffect, useState } from "react"
import {
  SyncCommitComponentQueryState,
  SyncCommitComponentResponse,
} from "./sync-commit-component.type"
import { getSyncCommitComponentListMiddleware } from "./sync-commit-component.api"
import SimpleTable, { SimpleTableProps } from "components/Table/SimpleTable"
import { useNumber, useString, useWindowSize } from "helpers/hooks"
import { cloneDeep, debounce } from "lodash"
import { Pagination } from "components/Pagination/Pagination"
import { ReactComponent as IconSearchNormal } from "assets/images/icons/icon-search-normal.svg"
import InputDefault from "components/Input/InputDefault"
import { ReactComponent as IconClearSearch } from "assets/images/icons/icon-clear-search.svg"
import { formatDate } from "helpers/utils"
import { Avatar } from "components/Avatar"
import { useAppSelector } from "hooks/useApp"
import { PERMISSIONS } from "constants/permissions"
import { hasPermission } from "helpers/permissions"
import { pushTo } from "helpers/history"
import { PATH } from "constants/path"

export const SyncCommitComponentPage = () => {
  const { isMobile } = useWindowSize()
  const userInfo = useAppSelector((state) => state.userInfo)
  const [syncCommitComponents, setSyncCommitComponents] = useState<
    SyncCommitComponentResponse[]
  >([])

  const [queryState, setQueryState] = useState<SyncCommitComponentQueryState>({
    page: 1,
    pageSize: 25,
    search: "",
  })
  const totalPage = useNumber(0)
  const search = useString()
  useEffect(() => {
    if (!userInfo?.id) {
      return
    }
    if (!userInfo.last_organization_id) {
      pushTo(PATH.notFound)
      return
    }
    if (!hasPermission(userInfo, PERMISSIONS.SYNC_COMMIT_COMPONENT_VIEW)) {
      pushTo(PATH.notFound)
      return
    }
  }, [userInfo?.id])

  useEffect(() => {
    getSyncCommitComponentList(queryState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryState.pageSize, queryState.page, queryState.search])

  const getSyncCommitComponentList = async (
    newQueryState: SyncCommitComponentQueryState
  ) => {
    const response = await getSyncCommitComponentListMiddleware(newQueryState)
    setSyncCommitComponents(response.data)
    totalPage.setValue(response.total)
  }

  const handleChangePage = (newPage: number, newLimit: number) => {
    setQueryState({
      ...queryState,
      page: newPage,
      pageSize: newLimit,
    })
  }

  const onPreventBubble = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceSearch = useCallback(
    debounce(
      (newSearch: string, newQueryState: SyncCommitComponentQueryState) => {
        setQueryState({
          ...newQueryState,
          search: newSearch.trim(),
          page: 1,
        })
      },
      500
    ),
    []
  )
  const columnsMobile: SimpleTableProps["columns"] = [
    {
      title: "From",
      dataIndex: "from",
      render: (columnData: SyncCommitComponentResponse) => {
        return (
          <div className="flex flex-col gap-[8px]">
            <p className="font-normal text-[13px] leading-[22px] text-[#111111]">
              From: <span className="font-semibold">{columnData.from}</span>
            </p>
            <p className="font-normal text-[13px] leading-[22px] text-[#111111]">
              To: <span className="font-semibold">{columnData.to}</span>
            </p>
            <p className="font-normal text-[13px] leading-[22px] text-[#111111]">
              Type:{" "}
              <span className="font-semibold text-[#56A012]">
                {columnData.type}
              </span>
            </p>
            <div className="flex items-center gap-[8px]">
              <Avatar
                width="24px"
                height="24px"
                name={columnData.user_fullname}
                avatar={columnData.user_avatar}
              />
              <p>{columnData.user_fullname || columnData.user_email}</p>
            </div>
            <p className="font-normal text-[13px] leading-[22px] text-[#111111]">
              Created:{" "}
              <span className="font-semibold">
                {columnData.created_at
                  ? formatDate(columnData.created_at, "DD MMM yyyy - HH:mm A")
                  : "-"}
              </span>
            </p>
          </div>
        )
      },
    },
  ]

  const columns: SimpleTableProps["columns"] = [
    {
      title: "From",
      dataIndex: "from",
      render: (columnData: SyncCommitComponentResponse) => (
        <p className="pl-[20px]">{columnData.from}</p>
      ),
      width: `40%`,
    },
    {
      title: "To",
      dataIndex: "to",
      render: (columnData: SyncCommitComponentResponse) => (
        <p>{columnData.to}</p>
      ),
      width: `20%`,
    },
    {
      title: "Type",
      dataIndex: "type",
      render: (columnData: SyncCommitComponentResponse) => (
        <p
          className="font-semibold"
          style={{
            color: "#56A012",
          }}
        >
          {columnData.type}
        </p>
      ),
      width: `10%`,
    },
    {
      title: "Created By",
      dataIndex: "created_by",
      render: (columnData: SyncCommitComponentResponse) => {
        return (
          <div className="flex items-center gap-[8px]">
            <Avatar
              width="24px"
              height="24px"
              name={columnData.user_fullname}
              avatar={columnData.user_avatar}
            />
            <p>{columnData.user_fullname || columnData.user_email}</p>
          </div>
        )
      },
      width: `15%`,
    },
    {
      title: "Created",
      dataIndex: "created_at",
      render: (columnData: SyncCommitComponentResponse) => (
        <p>
          {columnData.created_at
            ? formatDate(columnData.created_at, "DD MMM yyyy - HH:mm A")
            : "-"}
        </p>
      ),
      width: "15%",
    },
  ]

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex items-center justify-between bg-[#FFFFFF] border-b border-[#E4E4E4] min-h-[63px] px-[24px]">
        <h2 className="text-[16px] font-semibold lh-26 text-black">
          Sync histories
        </h2>
        <div className="flex items-center gap-[12px]">
          {!isMobile ? (
            <div
              className=" bg-grayWhite2 p-2 rounded-full flex items-center"
              onClick={onPreventBubble}
              style={{
                width: isMobile ? 200 : 320,
              }}
            >
              <IconSearchNormal
                style={{
                  width: 20,
                }}
                values={search.value}
                onClick={onPreventBubble}
                onChange={(e) => {
                  onPreventBubble(e)
                }}
              />
              <InputDefault
                value={search.value}
                placeholder="Search from, to or type..."
                style={{
                  border: "none",
                  boxShadow: "none",
                  height: 30,
                  fontWeight: 400,
                }}
                classCustom="h-11 border rounded bg-grayWhite2 pl-3 pr-3 text-sm font-light text-[#111111] focus:outline-none"
                onClick={onPreventBubble}
                onChange={(e) => {
                  onPreventBubble(e)
                  search.setValue(e.target.value)
                  debounceSearch(e.target.value, cloneDeep(queryState))
                }}
              />
              {search.value ? (
                <div
                  onClick={(e) => {
                    onPreventBubble(e)
                    search.setValue("")
                    setQueryState({
                      ...queryState,
                      search: "",
                    })
                  }}
                  className="h-5 w-5 flex items-center justify-center custom-icon-clear-search cursor-pointer"
                >
                  <IconClearSearch />
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
      <div className="flex flex-col py-6 pl-6 flex-1 overflow-hidden">
        <div className="flex flex-col pr-6 overflow-auto gap-6">
          <div className="text-black bg-white border border-border-gray rounded-md custom-table">
            <SimpleTable
              customClass="storage-usage-table"
              columns={isMobile ? columnsMobile : columns}
              dataSource={syncCommitComponents || []}
              isRequiredTableHeader={isMobile ? false : true}
            />
          </div>
          {totalPage.value ? (
            <Pagination
              total={totalPage.value}
              queryState={{
                page: queryState.page,
                limit: queryState.pageSize,
              }}
              handleChangePage={handleChangePage}
            />
          ) : null}
        </div>
      </div>
    </div>
  )
}
